import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { useRegenerateDevicePreviews } from '../hooks/useRegeneratePreviews';
import { useDispatch } from 'react-redux';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { Refresh24 } from 'ui/icons/aurora/24px/Refresh24';
import { useState } from 'react';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';

interface RegeneratePreviewsProps {
  templateId: number;
  locale: string;
  refetch: () => void;
  isDisabled?: boolean;
}

export const RegeneratePreviews = ({
  templateId,
  locale,
  refetch,
  isDisabled = false,
}: RegeneratePreviewsProps) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate, isLoading } = useRegenerateDevicePreviews(
    { templateId: templateId.toString(), ...(locale && { locale }) },
    {
      onError: (error) => {
        dispatch(addNegativeToast('Unable to regenerate previews. Please try again.', { error }));
      },
      onSuccess: () => {
        refetch();
        dispatch(addPositiveToast('Previews regenerated successfully.'));
      },
    },
  );

  return (
    <>
      <Button
        variant={BUTTON_VARIANTS.SECONDARY}
        isLoading={isLoading}
        onClick={() => setIsModalOpen(true)}
        data-test="regenerate-previews-button"
        isDisabled={isDisabled}
      >
        <Refresh24 /> Regenerate previews
      </Button>
      {isModalOpen && (
        <Modal
          id="regenerate-previews-modal"
          onClose={() => setIsModalOpen(false)}
          isModalOpen
          size={MODAL_SIZES.SMALL}
        >
          <ModalHeader variant={MODAL_VARIANTS.WARNING}>Regenerate previews?</ModalHeader>
          <ModalBody>
            Are you sure you want to regenerate these previews? This can take several minutes.
          </ModalBody>
          <ModalFooter onClose={() => setIsModalOpen(false)}>
            <Button
              size={BUTTON_SIZES.LARGE}
              onClick={() => {
                setIsModalOpen(false);
                mutate();
              }}
              data-test="regenerate-previews-button-confirm"
            >
              Regenerate previews
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};
